import { Component, ContextType, ReactNode } from 'react';
import { defineMessages, IntlContext } from 'dibs-react-intl';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import classnames from 'classnames';
import RecognizedIcon from 'dibs-icons/exports/RecognizedRibbon';
import TopSellerIcon from 'dibs-icons/exports/Diamond';
import DiamondFilled from 'dibs-icons/exports/DiamondFilled';
import { Link } from 'dibs-elements/exports/Link';

import AssociationsIcon from 'dibs-icons/exports/LeafCrown';
import { SellerReviewsModalAsync } from 'dibs-seller-reviews-modal/exports/SellerReviewsModalAsync';

import {
    recognizedTitle,
    recognizedDescription,
    goldSellerTitle,
    goldSellerDescription,
    platinumSellerTitle,
    diamondSellerTitle,
    tieredSellerDescription,
    associationsTitle,
    associationsDescription,
    vettedDescription,
} from '../messages/pdpDistinctionsMessages';
import { getIsNewSeller } from '../../utils/sellerHelpers';

import { SharedStarRating } from '../../shared/SharedStarRating/SharedStarRating';
import { PdpSharedNewSellerPill } from '../shared/PdpSharedNewSellerPill/PdpSharedNewSellerPill';
import { PdpRespSidebarSellerInfoTooltip } from './PdpRespSidebarSellerInfoTooltip';

import { PdpRespSidebarSellerInfo_user$data } from './__generated__/PdpRespSidebarSellerInfo_user.graphql';
import { PdpRespSidebarSellerInfo_seller$data } from './__generated__/PdpRespSidebarSellerInfo_seller.graphql';

import styles from './PdpRespSidebarSellerInfo.scss';

type Props = {
    user: PdpRespSidebarSellerInfo_user$data;
    seller: PdpRespSidebarSellerInfo_seller$data;
};

type State = {
    isReviewModalOpen: boolean;
    load: boolean;
    isVisible: boolean;
};

const messages = defineMessages({
    vettedTitle: {
        id: 'PdpRespSidebarSellerInfo.vettedTitle',
        defaultMessage: 'Vetted Professional Seller',
    },
    sellerReviews: {
        id: 'PdpRespSidebarSellerInfo.sellerReviews',
        defaultMessage: 'see seller reviews',
    },
    sellerTierLabel: {
        id: 'PdpRespSidebarSellerInfo.sellerTierLabel',
        defaultMessage: 'Seller Tier',
    },
    sellerAssociationsLabel: {
        id: 'PdpRespSidebarSellerInfo.sellerAssociations',
        defaultMessage: 'Seller Associations',
    },
    recognizedLabel: {
        id: 'PdpRespSidebarSellerInfo.recognizedLabel',
        defaultMessage: 'Recognized',
    },
});

class PdpRespSidebarSellerInfoComponent extends Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            isReviewModalOpen: false,
            load: false,
            isVisible: false,
        };

        this.openModal = this.openModal.bind(this);
        this.onClose = this.onClose.bind(this);
    }

    context!: ContextType<typeof IntlContext>;

    openModal(): void {
        this.setState({ isReviewModalOpen: true });
    }

    onClose(): void {
        this.setState({ isReviewModalOpen: false });
    }

    render(): ReactNode {
        const { seller, user } = this.props;
        const { isReviewModalOpen } = this.state;

        // Seller icons
        const {
            isDistinguished,
            sellerTiers,
            isRewardsTier2,
            serviceId,
            tradeAssociations,
            sellerProfile,
            monthsSinceStartDate,
            orderFeedbackPdp,
        } = seller || {};
        const { sellerTierDataNotFound, isGoldTier, isPlatinumTier } = sellerTiers || {};
        const sellerTierDataFound = !sellerTierDataNotFound;
        const isGoldSeller = sellerTierDataFound && isGoldTier;
        const isPlatinumSeller = sellerTierDataFound && isPlatinumTier;

        const isTopSeller = isGoldSeller || isPlatinumSeller || isRewardsTier2;
        let topSellerTitle = null;
        let topSellerDescription = tieredSellerDescription;
        let customIconStyles = '';
        if (isRewardsTier2) {
            topSellerTitle = diamondSellerTitle;
        } else if (isGoldSeller) {
            topSellerTitle = goldSellerTitle;
            topSellerDescription = goldSellerDescription;
            customIconStyles = styles.goldDiamond;
        } else if (isPlatinumSeller) {
            topSellerTitle = platinumSellerTitle;
            customIconStyles = styles.platinumDiamond;
        }

        const { isVerifiedTrade, canShowSellerName } = user || {};
        const { company } = sellerProfile || {};
        const sellerCompany = (isVerifiedTrade && canShowSellerName && company) || '';

        // Seller Since
        const isNewSeller = getIsNewSeller(monthsSinceStartDate);

        // Seller rating
        let feedbackRating =
            orderFeedbackPdp?.metadata?.aggregatedFeedback?.overallExperience?.mean;
        feedbackRating = feedbackRating ? parseFloat(feedbackRating.toFixed(1)) : 0;

        const tooltipProps = {
            wrapperClass: styles.tooltipWrapper,
            linkClass: styles.tooltipLink,
        };
        const starRating = <SharedStarRating seller={seller} />;
        const TierIcon = isRewardsTier2 ? DiamondFilled : TopSellerIcon;
        const distinction =
            feedbackRating === 0 && isNewSeller ? (
                <PdpSharedNewSellerPill />
            ) : (
                <>
                    {isTopSeller && (
                        <PdpRespSidebarSellerInfoTooltip
                            ariaLabel={this.context?.formatMessage(messages.sellerTierLabel)}
                            title={topSellerTitle}
                            description={topSellerDescription}
                            dataTn="pdp-sidebar-seller-top-tier"
                            {...tooltipProps}
                        >
                            <TierIcon className={classnames(styles.icon, customIconStyles)} />
                        </PdpRespSidebarSellerInfoTooltip>
                    )}
                    {isDistinguished && (
                        <PdpRespSidebarSellerInfoTooltip
                            ariaLabel={this.context?.formatMessage(
                                messages.sellerAssociationsLabel
                            )}
                            title={recognizedTitle}
                            description={recognizedDescription}
                            dataTn="pdp-sidebar-seller-recognized"
                            {...tooltipProps}
                        >
                            <RecognizedIcon className={styles.icon} />
                        </PdpRespSidebarSellerInfoTooltip>
                    )}
                    {(tradeAssociations || []).length ? (
                        <PdpRespSidebarSellerInfoTooltip
                            ariaLabel={this.context?.formatMessage(messages.recognizedLabel)}
                            title={associationsTitle}
                            description={associationsDescription}
                            dataTn="pdp-sidebar-seller-associations"
                            {...tooltipProps}
                        >
                            <AssociationsIcon className={styles.icon} />
                        </PdpRespSidebarSellerInfoTooltip>
                    ) : null}
                </>
            );

        const feedbackElement =
            feedbackRating > 0 ? (
                <>
                    <Link
                        ariaLabel={this.context?.formatMessage(messages.sellerReviews)}
                        className={styles.ratingWrapper}
                        onClick={this.openModal}
                        dataTn="pdp-sidebar-seller-rating-link"
                    >
                        {starRating}
                    </Link>

                    <SellerReviewsModalAsync
                        isOpen={isReviewModalOpen}
                        onClose={this.onClose}
                        sellerId={serviceId}
                    />
                </>
            ) : (
                starRating
            );

        return (
            <div className={styles.container} data-tn="pdp-sidebar-seller-info">
                <div className={styles.wrapper}>
                    <PdpRespSidebarSellerInfoTooltip
                        description={vettedDescription}
                        {...tooltipProps}
                    >
                        {sellerCompany || this.context?.formatMessage(messages.vettedTitle)}
                    </PdpRespSidebarSellerInfoTooltip>
                    {distinction}
                </div>
                {feedbackElement}
            </div>
        );
    }
}

PdpRespSidebarSellerInfoComponent.contextType = IntlContext;

export const PdpRespSidebarSellerInfo = createFragmentContainer(PdpRespSidebarSellerInfoComponent, {
    user: graphql`
        fragment PdpRespSidebarSellerInfo_user on User {
            isVerifiedTrade
            canShowSellerName
        }
    `,
    seller: graphql`
        fragment PdpRespSidebarSellerInfo_seller on Seller {
            ...SharedStarRating_seller
            monthsSinceStartDate: timeSinceStartDate(unit: MONTH)
            serviceId
            isDistinguished
            isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
            sellerTiers {
                isGoldTier
                isPlatinumTier
                sellerTierDataNotFound
            }
            orderFeedbackPdp {
                metadata {
                    aggregatedFeedback {
                        overallExperience {
                            mean
                        }
                    }
                }
            }
            sellerProfile {
                company
            }
            tradeAssociations {
                serviceId
            }
        }
    `,
});
